import { useState, useEffect, useRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Button, IconButton, Checkbox, FormControlLabel, ToggleButton, ToggleButtonGroup,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen'
import HistoryIcon from '@mui/icons-material/History'
import { useNotes } from '@/hooks/useNotes'
import { useCustomViewNotes } from '@/hooks/useCustomViewNotes'
import { useActiveSharedView } from '@/hooks/useActiveSharedView'
import { NotesFab } from './NotesFab'
import { NoteHistoryDialog } from './NoteHistoryDialog'
import {
  NoteScopeEditor,
  type NoteScope,
  type NoteScopeEditorHandle,
  type NoteScopeState,
} from './NoteScopeEditor'
import { useAppStore } from '@/store/appStore'
import type { ContentType } from '@/types/api'

import { useIsExpanded } from '@/hooks/useLayoutMode'
import { useNavigationWarning } from '@/hooks/useNavigationWarning'

interface NotePanelProps {
  contentType: ContentType
  entityId: number
  /** True when the panel is detached into the floating (non-modal) window. */
  popped: boolean
  onPoppedChange: (popped: boolean) => void
  onClose: () => void
}

const TITLE_ID = 'note-panel-title'
/** Debounce before a resize is written to the persisted store. */
const GEOMETRY_COMMIT_MS = 250
/** Gap kept between the floating window and the viewport edges. */
const WINDOW_MARGIN = 16
const DEFAULT_WINDOW_WIDTH = 560
const DEFAULT_WINDOW_HEIGHT = 380
/** Docked panel height bounds, measured over the whole panel (header included). */
const MIN_DOCKED_HEIGHT = 200
const MAX_DOCKED_HEIGHT = 800
/** Keyboard resize step for the docked panel's separator. */
const DOCKED_HEIGHT_STEP = 16
/** Content kept visible above the docked panel on short viewports. */
const MIN_CONTENT_HEIGHT = 120

const IDLE_STATE: NoteScopeState = { dirty: false, saving: false, status: 'idle' }

type ScopeMode = NoteScope | 'both'

const SCOPES_BY_MODE: Record<ScopeMode, NoteScope[]> = {
  personal: ['personal'],
  view: ['view'],
  both: ['personal', 'view'],
}

type WindowRect = { x: number; y: number; width: number; height: number }

function clampDockedHeight(height: number, max: number = MAX_DOCKED_HEIGHT): number {
  return Math.min(Math.max(max, MIN_DOCKED_HEIGHT), Math.max(MIN_DOCKED_HEIGHT, Math.round(height)))
}

/** Centred starting geometry, used the first time the window is ever opened. */
function defaultRect(): WindowRect {
  const width = Math.min(DEFAULT_WINDOW_WIDTH, window.innerWidth - WINDOW_MARGIN * 2)
  const height = Math.min(DEFAULT_WINDOW_HEIGHT, window.innerHeight - WINDOW_MARGIN * 2)
  return {
    width,
    height,
    x: Math.max(0, Math.round((window.innerWidth - width) / 2)),
    y: Math.max(0, Math.round((window.innerHeight - height) / 2)),
  }
}

/**
 * A restored rect can be off-screen or oversized when the viewport has changed
 * since it was saved (smaller display, resized browser), so pull it back in.
 */
function clampToViewport(rect: WindowRect | null | undefined): WindowRect {
  if (!rect) return defaultRect()
  const width = Math.min(rect.width, window.innerWidth - WINDOW_MARGIN * 2)
  const height = Math.min(rect.height, window.innerHeight - WINDOW_MARGIN * 2)
  return {
    width,
    height,
    x: Math.min(Math.max(0, rect.x), Math.max(0, window.innerWidth - width)),
    y: Math.min(Math.max(0, rect.y), Math.max(0, window.innerHeight - height)),
  }
}

export function NotePanel({ contentType, entityId, popped, onPoppedChange, onClose }: NotePanelProps) {
  const isExpandedLayout = useIsExpanded()
  const { t } = useTranslation()
  const { getNotesFor } = useNotes()
  const autosave = useAppStore((s) => s.notesAutosave)
  const setAutosave = useAppStore((s) => s.setNotesAutosave)
  const personalNote = getNotesFor(contentType, entityId)[0] ?? null

  // Notes on a shared view live with the view, not the user, so they are a second scope here.
  const sharedView = useActiveSharedView()
  const [scopeMode, setScopeMode] = useState<ScopeMode>('personal')
  const viewNotes = useCustomViewNotes(sharedView?.id ?? null, sharedView !== null, `${contentType}:${entityId}`)
  const viewNote = sharedView !== null ? viewNotes.getNoteFor(contentType, entityId) : null
  const visibleScopes = sharedView ? SCOPES_BY_MODE[scopeMode] : SCOPES_BY_MODE.personal
  const viewVisible = visibleScopes.includes('view')
  const bothVisible = visibleScopes.length > 1

  const personalRef = useRef<NoteScopeEditorHandle>(null)
  const viewRef = useRef<NoteScopeEditorHandle>(null)
  const [scopeStates, setScopeStates] = useState<Record<NoteScope, NoteScopeState>>({
    personal: IDLE_STATE,
    view: IDLE_STATE,
  })
  const handleScopeState = useCallback((scope: NoteScope, next: NoteScopeState) => {
    setScopeStates((prev) => ({ ...prev, [scope]: next }))
  }, [])

  // The view pane unmounts with the shared view, and its last report must not outlive it.
  const personalState = scopeStates.personal
  const viewState = sharedView ? scopeStates.view : IDLE_STATE
  const states = [personalState, viewState]
  const anyDirty = states.some((s) => s.dirty)
  const anySaving = states.some((s) => s.saving)
  const anyError = states.some((s) => s.status === 'error')
  const anySaved = states.some((s) => s.status === 'saved')

  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false)
  const [historyOpen, setHistoryOpen] = useState(false)
  // Geometry is restored from the persisted preference, so the window reopens
  // exactly where — and at the size — the user last left it.
  const savedRect = useAppStore((s) => s.notesWindowRect)
  const setSavedRect = useAppStore((s) => s.setNotesWindowRect)
  const [windowRect, setWindowRect] = useState<WindowRect>(() => clampToViewport(savedRect))
  const savedDockedHeight = useAppStore((s) => s.notesDockedHeight)
  const setSavedDockedHeight = useAppStore((s) => s.setNotesDockedHeight)
  const [dockedHeight, setDockedHeight] = useState(() => clampDockedHeight(savedDockedHeight))
  const dockDrag = useRef<{ startY: number; startHeight: number } | null>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const dragState = useRef<{ paper: HTMLElement; dx: number; dy: number } | null>(null)

  // Published app-wide so navigation and filter changes can stop and ask before discarding it.
  const setNotesDirty = useAppStore((s) => s.setNotesDirty)
  useEffect(() => {
    setNotesDirty(anyDirty)
    return () => setNotesDirty(false)
  }, [anyDirty, setNotesDirty])

  // Browser back/forward and tab close cannot be caught by the in-app guard.
  useNavigationWarning(anyDirty, t('notes.unsavedNavMessage'))

  const mountedRefs = () => [personalRef.current, sharedView ? viewRef.current : null]
    .filter((r): r is NoteScopeEditorHandle => r !== null)

  const saveAll = async () => {
    const results = await Promise.all(mountedRefs().map((r) => r.save()))
    return results.every(Boolean)
  }

  // Bringing the shared scope on screen is a deliberate "show me what the team has" action, so it
  // is the other point worth paying for a re-read at.
  const handleScopeModeChange = (next: ScopeMode) => {
    const added = SCOPES_BY_MODE[next].find((s) => !visibleScopes.includes(s))
    setScopeMode(next)
    if (added === 'view') viewNotes.refetch()
    if (added) requestAnimationFrame(() => (added === 'view' ? viewRef : personalRef).current?.focus())
  }

  const handleAutosaveToggle = (checked: boolean) => {
    setAutosave(checked)
    if (checked) void saveAll()
  }

  // Read the live geometry straight off the paper and persist it. The debounced
  // ResizeObserver below is a background safety net; every path that tears the
  // window down calls this first, while the DOM node is still measurable.
  const persistGeometry = useCallback(() => {
    const paper = panelRef.current?.closest('.MuiDialog-paper') as HTMLElement | null
    if (!paper) return
    const rect = paper.getBoundingClientRect()
    if (!rect.width || !rect.height) return
    const next = {
      x: Math.round(rect.left),
      y: Math.round(rect.top),
      width: Math.round(rect.width),
      height: Math.round(rect.height),
    }
    setSavedRect(next)
    setWindowRect(next)
  }, [setSavedRect])

  // A delete empties one pane. With the other pane still on screen the panel stays; alone, it
  // closes as before, unless a hidden pane is still holding unsaved text.
  const handleDeleted = (scope: NoteScope) => {
    const otherDirty = (scope === 'personal' ? viewState : personalState).dirty
    if (bothVisible || otherDirty) return
    if (popped) persistGeometry()
    onClose()
  }

  // The floating window only ever closes through its own buttons, and never
  // while there is unsaved work — the confirmation dialog owns that decision.
  const requestClose = () => {
    if (popped) persistGeometry()
    if (popped && anyDirty) {
      setConfirmCloseOpen(true)
      return
    }
    onClose()
  }

  const handlePoppedToggle = () => {
    if (popped) persistGeometry()
    onPoppedChange(!popped)
  }

  const handleConfirmSaveAndClose = async () => {
    const ok = await saveAll()
    setConfirmCloseOpen(false)
    if (ok) onClose()
  }

  const handleConfirmDiscard = () => {
    mountedRefs().forEach((r) => r.discard())
    setConfirmCloseOpen(false)
    onClose()
  }

  // 800px is the ceiling, but on a short viewport the panel would grow past its
  // flex container and clip its own action bar, so leave room for the content.
  const availableDockedHeight = () => {
    const container = panelRef.current?.parentElement
    if (!container) return MAX_DOCKED_HEIGHT
    return Math.min(MAX_DOCKED_HEIGHT, container.clientHeight - MIN_CONTENT_HEIGHT)
  }

  // Docked panel: the separator on its top edge drags the panel taller or
  // shorter. Height is committed to the store on release, not on every move.
  const handleDockResizeStart = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return
    dockDrag.current = { startY: e.clientY, startHeight: dockedHeight }
    e.currentTarget.setPointerCapture(e.pointerId)
    e.preventDefault()
  }

  const handleDockResizeMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = dockDrag.current
    if (!drag) return
    // The panel is anchored to the bottom, so dragging upwards makes it taller.
    setDockedHeight(clampDockedHeight(drag.startHeight + (drag.startY - e.clientY), availableDockedHeight()))
  }

  const handleDockResizeEnd = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dockDrag.current) return
    dockDrag.current = null
    e.currentTarget.releasePointerCapture(e.pointerId)
    setSavedDockedHeight(dockedHeight)
  }

  const handleDockResizeKey = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const delta = e.key === 'ArrowUp' ? DOCKED_HEIGHT_STEP : e.key === 'ArrowDown' ? -DOCKED_HEIGHT_STEP : 0
    if (!delta) return
    e.preventDefault()
    const next = clampDockedHeight(dockedHeight + delta, availableDockedHeight())
    setDockedHeight(next)
    setSavedDockedHeight(next)
  }

  // Dragging the floating window by its header. Position stays unset until the
  // first drag so MUI's own centring is used for the initial placement.
  const handleDragStart = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!popped || e.button !== 0) return
    if ((e.target as HTMLElement).closest('button, input, label, textarea')) return
    const paper = (e.currentTarget as HTMLElement).closest('.MuiDialog-paper') as HTMLElement | null
    if (!paper) return
    const rect = paper.getBoundingClientRect()
    dragState.current = { paper, dx: e.clientX - rect.left, dy: e.clientY - rect.top }
    setWindowRect({ x: rect.left, y: rect.top, width: rect.width, height: rect.height })
    e.currentTarget.setPointerCapture(e.pointerId)
  }

  const handleDragMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragState.current
    if (!drag) return
    const maxX = window.innerWidth - drag.paper.offsetWidth
    const maxY = window.innerHeight - drag.paper.offsetHeight
    setWindowRect({
      x: Math.min(Math.max(0, e.clientX - drag.dx), Math.max(0, maxX)),
      y: Math.min(Math.max(0, e.clientY - drag.dy), Math.max(0, maxY)),
      width: drag.paper.offsetWidth,
      height: drag.paper.offsetHeight,
    })
  }

  const handleDragEnd = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragState.current
    if (!drag) return
    dragState.current = null
    e.currentTarget.releasePointerCapture(e.pointerId)
    const rect = drag.paper.getBoundingClientRect()
    setSavedRect({ x: rect.left, y: rect.top, width: rect.width, height: rect.height })
  }

  // The paper carries a native `resize: both` handle, so size changes arrive
  // through the DOM rather than React. Mirror them into state (so a later drag
  // does not re-apply a stale size) and into the persisted preference.
  useEffect(() => {
    if (!popped) return
    const paper = panelRef.current?.closest('.MuiDialog-paper') as HTMLElement | null
    if (!paper) return
    let timer: ReturnType<typeof setTimeout> | null = null
    const commit = () => {
      if (timer) clearTimeout(timer)
      timer = null
      const rect = paper.getBoundingClientRect()
      const next = {
        x: Math.round(rect.left),
        y: Math.round(rect.top),
        width: Math.round(rect.width),
        height: Math.round(rect.height),
      }
      setSavedRect(next)
      // Mirror into state as well: the docked/popped toggle does not remount
      // this component, so state — not the store — feeds the next render.
      setWindowRect(next)
    }
    const observer = new ResizeObserver(() => {
      if (dragState.current) return
      if (timer) clearTimeout(timer)
      timer = setTimeout(commit, GEOMETRY_COMMIT_MS)
    })
    observer.observe(paper)
    // Releasing the resize handle flushes immediately, so a resize is never
    // lost to the debounce when the note is closed straight afterwards.
    paper.addEventListener('pointerup', commit)
    return () => {
      observer.disconnect()
      paper.removeEventListener('pointerup', commit)
      if (timer) clearTimeout(timer)
    }
  }, [popped, setSavedRect])

  // A scope that is off screen gets a dot when there is something there worth coming back to.
  const scopeDot = (scope: NoteScope, hasNote: boolean, dirty: boolean) =>
    !visibleScopes.includes(scope) && (hasNote || dirty) && (
      <span
        className={`note-panel-scope-dot${dirty ? ' note-panel-scope-dot--unsaved' : ''}`}
        role="img"
        aria-label={dirty ? t('notes.unsavedChanges') : t('notes.scopeHasNote')}
      />
    )

  const panel = (
    <div
      ref={panelRef}
      className={`note-panel${popped ? ' note-panel--popped' : ''}`}
      role="region"
      aria-labelledby={TITLE_ID}
      // Below lg the docked height is additionally capped at 60dvh: a panel sized on
      // a desktop would otherwise swallow a phone viewport. The store keeps the
      // desktop value; only the rendered height is clamped.
      style={popped ? undefined : { height: dockedHeight, ...(isExpandedLayout ? {} : { maxHeight: '60dvh' }) }}
    >
      {/* Anchored to the panel's own box, so resizing moves both together in a
          single layout pass instead of the button chasing the panel. */}
      {!popped && (
        <NotesFab
          contentType={contentType}
          entityId={entityId}
          noteOpen
          anchored
          onToggle={requestClose}
        />
      )}
      {!popped && (
        <div
          className="note-panel-resizer"
          role="separator"
          aria-orientation="horizontal"
          aria-label={t('notes.resizePanel')}
          aria-valuenow={dockedHeight}
          aria-valuemin={MIN_DOCKED_HEIGHT}
          aria-valuemax={MAX_DOCKED_HEIGHT}
          tabIndex={0}
          onPointerDown={handleDockResizeStart}
          onPointerMove={handleDockResizeMove}
          onPointerUp={handleDockResizeEnd}
          onPointerCancel={handleDockResizeEnd}
          onKeyDown={handleDockResizeKey}
        />
      )}
      <div
        className={`note-panel-header${popped ? ' note-panel-header--draggable' : ''}`}
        onPointerDown={handleDragStart}
        onPointerMove={handleDragMove}
        onPointerUp={handleDragEnd}
        onPointerCancel={handleDragEnd}
      >
        <div className="note-panel-header-left">
          {/* Summarises every pane, hidden ones included, so unsaved text in a deselected scope
              is still announced here. */}
          <span className="note-panel-title" id={TITLE_ID}>
            {t('notes.title')}
            {anySaving
              ? <span className="note-panel-saving-indicator"> · {t('notes.saving')}</span>
              : anyError
                ? <span className="note-panel-error-indicator"> · {t('notes.saveFailed')}</span>
                : anyDirty
                  ? <span className="note-panel-unsaved-indicator"> · {t('notes.unsavedChanges')}</span>
                  : anySaved && <span className="note-panel-saved-indicator"> · {t('notes.saved')}</span>}
          </span>
          {viewVisible && (
            <span className="note-panel-scope-hint">
              {!viewNotes.canEdit ? t('notes.viewScopeReadOnly') : t('notes.viewScopeHint', { name: sharedView?.name ?? '' })}
            </span>
          )}
          {/* Who last touched a shared note is the cheapest possible collision warning: it is
              already in the DTO, and seeing a colleague's name here stops the overwrite before
              it is typed. */}
          {viewVisible && viewNote?.updatedBy && (
            <span className="note-panel-attribution">
              {t('notes.lastEditedBy', { name: viewNote.updatedBy })}
            </span>
          )}
        </div>
        <div className="note-panel-header-right">
          {sharedView && (
            <ToggleButtonGroup
              className="note-panel-scope"
              exclusive
              size="small"
              value={scopeMode}
              onChange={(_, next: ScopeMode | null) => next && handleScopeModeChange(next)}
            >
              <ToggleButton value="personal">
                {t('notes.scopePersonal')}
                {scopeDot('personal', personalNote !== null, personalState.dirty)}
              </ToggleButton>
              <ToggleButton value="view">
                {t('notes.scopeView')}
                {scopeDot('view', viewNote !== null, viewState.dirty)}
              </ToggleButton>
              <ToggleButton value="both">{t('notes.scopeBoth')}</ToggleButton>
            </ToggleButtonGroup>
          )}
          <FormControlLabel
            className="note-panel-autosave-label"
            label={t('notes.autosave')}
            control={
              <Checkbox
                checked={autosave}
                onChange={(e) => handleAutosaveToggle(e.target.checked)}
              />
            }
          />
          {viewVisible && viewNote && (
            <IconButton
              className="note-panel-history"
              onClick={() => setHistoryOpen(true)}
              aria-label={t('notes.historyTitle')}
              title={t('notes.historyTitle')}
              size="small"
            >
              <HistoryIcon fontSize="small" />
            </IconButton>
          )}
          {/* Pop-out is a desktop affordance: the floating window cannot be dragged
              or resized by touch, so below lg the panel is docked and the toggle hidden. */}
          {isExpandedLayout && (
          <IconButton
            className="note-panel-pop"
            onClick={handlePoppedToggle}
            aria-label={popped ? t('notes.dockNotes') : t('notes.popOutNotes')}
            title={popped ? t('notes.dockNotes') : t('notes.popOutNotes')}
            size="small"
          >
            {popped ? <CloseFullscreenIcon fontSize="small" /> : <OpenInNewIcon fontSize="small" />}
          </IconButton>
          )}
          <IconButton
            className="note-panel-close"
            onClick={requestClose}
            aria-label={t('notes.closeNote')}
            title={t('notes.closeNote')}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>
      </div>

      <div className={`note-panel-panes${bothVisible ? ' note-panel-panes--split' : ''}`}>
        <NoteScopeEditor
          ref={personalRef}
          scope="personal"
          contentType={contentType}
          entityId={entityId}
          sharedViewId={null}
          autosave={autosave}
          showLabel={bothVisible}
          hidden={!visibleScopes.includes('personal')}
          autoFocus={visibleScopes[0] === 'personal'}
          onStateChange={handleScopeState}
          onDeleted={handleDeleted}
        />
        {sharedView && (
          <NoteScopeEditor
            ref={viewRef}
            scope="view"
            contentType={contentType}
            entityId={entityId}
            sharedViewId={sharedView.id}
            autosave={autosave}
            showLabel={bothVisible}
            hidden={!viewVisible}
            autoFocus={visibleScopes[0] === 'view'}
            onStateChange={handleScopeState}
            onDeleted={handleDeleted}
          />
        )}
      </div>
    </div>
  )

  const historyDialog = (
    <NoteHistoryDialog
      open={historyOpen}
      viewId={sharedView?.id ?? null}
      contentType={contentType}
      entityId={entityId}
      onClose={() => setHistoryOpen(false)}
      onRestore={(restored) => viewRef.current?.restore(restored)}
    />
  )

  if (!popped) return <>{panel}{historyDialog}</>

  return (
    <>
      {/* Non-modal: no backdrop, no focus trap, no scroll lock, and pointer
          events pass straight through to the page except on the paper itself.
          `onClose` is deliberately omitted so neither a backdrop click nor Esc
          can dismiss it — only the header buttons close the window. */}
      <Dialog
        open
        hideBackdrop
        disableEnforceFocus
        disableScrollLock
        disableEscapeKeyDown
        /* `false` drops MUI's 600px `paperWidthSm` cap, which otherwise wins the
           cascade over the stylesheet and blocks horizontal resizing. */
        maxWidth={false}
        aria-labelledby={TITLE_ID}
        slotProps={{
          root: { sx: { pointerEvents: 'none' } },
          // `display: block` defeats the container's flex centring; the paper
          // is positioned explicitly instead, so the native resize handle
          // moves only the bottom-right corner rather than growing the box
          // outwards from its centre.
          container: { sx: { display: 'block' } },
          paper: {
            className: 'note-panel-window',
            // Geometry is always inline: it outranks every MUI paper rule, and
            // it is what the resize handle and the drag handler both write to.
            style: {
              position: 'fixed',
              left: windowRect.x,
              top: windowRect.y,
              width: windowRect.width,
              height: windowRect.height,
              // Anchored top-left, so the room left to grow is what remains
              // between the window's own edge and the viewport edge.
              maxWidth: window.innerWidth - windowRect.x,
              maxHeight: window.innerHeight - windowRect.y,
              margin: 0,
            },
          },
        }}
      >
        {panel}
      </Dialog>

      <Dialog
        open={confirmCloseOpen}
        onClose={() => setConfirmCloseOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('notes.unsavedTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('notes.unsavedMessage')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmCloseOpen(false)}>{t('notes.keepEditing')}</Button>
          <Button onClick={handleConfirmDiscard} color="error">{t('notes.discardChanges')}</Button>
          <Button onClick={handleConfirmSaveAndClose} variant="contained" disabled={anySaving} autoFocus>
            {t('notes.saveAndClose')}
          </Button>
        </DialogActions>
      </Dialog>

      {historyDialog}
    </>
  )
}
